/**
 * Media validation utilities for restricting file uploads across the UI to
 * images, animations, and videos used by creators.
 */

export const ALLOWED_IMAGE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'avif',
  'svg', 'bmp', 'ico', 'tiff', 'tif', 'apng'
];

export const ALLOWED_VIDEO_EXTENSIONS = [
  'mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', 'wmv', 'flv',
  '3gp', 'ogv', 'ts', 'mts'
];

/**
 * Standard accept attribute value for HTML file inputs
 * Covers both MIME type wildcards and explicit file extensions for maximum cross-browser/OS support.
 */
export const ALLOWED_MEDIA_ACCEPT =
  'image/*,video/*,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.avif,.svg,.bmp,.ico,.mp4,.mov,.webm,.mkv,.avi,.m4v,.wmv,.flv,.3gp,.ogv';

export const MEDIA_RESTRICTION_ERROR_MESSAGE =
  'Unsupported file format. Please upload a supported file type (e.g. JPG, PNG, GIF, WebP, MP4, MOV).';

export const SUPPORTED_FORMATS_TEXT =
  'Supported formats: JPG, PNG, GIF, WebP, MP4, MOV';

export const MAX_FILE_SIZE_TEXT =
  'File must be less than 500MB';

/**
 * Extract lowercased extension without the leading dot.
 * @param {string} filename
 * @returns {string}
 */
export const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase().trim() : '';
};

/**
 * Checks if a file is an allowed image or animation.
 * @param {File|Blob|{type?: string, name?: string}} file
 * @returns {boolean}
 */
export const isImageFile = (file) => {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/')) {
    return true;
  }
  const ext = getFileExtension(file.name);
  return ALLOWED_IMAGE_EXTENSIONS.includes(ext);
};

/**
 * Checks if a file is an allowed video.
 * @param {File|Blob|{type?: string, name?: string}} file
 * @returns {boolean}
 */
export const isVideoFile = (file) => {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('video/')) {
    return true;
  }
  const ext = getFileExtension(file.name);
  return ALLOWED_VIDEO_EXTENSIONS.includes(ext);
};

/**
 * Validates that a file is either an image, animation, or video.
 * Disallows documents (.pdf, .docx, .txt), archives (.zip, .rar), executables, etc.
 * @param {File|Blob|{type?: string, name?: string}} file
 * @returns {boolean}
 */
export const isMediaFile = (file) => {
  if (!file) return false;
  return isImageFile(file) || isVideoFile(file);
};
